import { CITIZENSHIP_KEY } from '../../../hooks/useCandidateProfile'
import { filterOutNewOrDeletedCitizenships } from './formatHelpers'

export const createWorkAuthOnSuccess = ({
  data,
  queryClient,
  candidateId,
  citizenships,
  reset,
  formatInitialValues,
  trackCitizenshipAddition,
  citizenshipsToDelete,
}) => {
  const newCitizenships = data.map(gqlResponseData => {
    const newCitizenship = gqlResponseData.createCandidateRegionalCitizenship.regionalCitizenship
    trackCitizenshipAddition({
      citizenshipStatus: newCitizenship.citizenshipStatus,
      countryName: newCitizenship.countryName,
      citizenshipId: newCitizenship.id,
    })
    return newCitizenship
  })
  if (newCitizenships.length > 0) {
    const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
    const citizenshipsToSet = [citizenships]

    const existingCitizenships = filterOutNewOrDeletedCitizenships({
      citizenshipsToDelete,
      regionalCitizenships: cachedQueryData.candidateProfile.regionalCitizenships,
    })

    cachedQueryData.candidateProfile.regionalCitizenships = [...existingCitizenships, ...newCitizenships]

    const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
      regionalCitizenship => regionalCitizenship.countryIso2Code,
    )

    citizenshipsToSet.forEach(citizenship => {
      if (!existingCountryCodes.includes(citizenship.value)) {
        cachedQueryData.candidateProfile.regionalCitizenships.push({
          countryName: citizenship.label,
          countryIso2Code: citizenship.value,
          citizenshipStatus: 'CITIZEN',
        })
      }
    })

    queryClient.setQueryData(([CITIZENSHIP_KEY, candidateId], cachedQueryData))
    reset(
      formatInitialValues({
        regionalCitizenshipsData: cachedQueryData.candidateProfile.regionalCitizenships,
        shouldExcludeNoId: false,
      }),
    )
  }
}

export const updateWorkAuthOnSuccess = ({
  data,
  editingCitizenshipIds,
  queryClient,
  candidateId,
  setEditingCitizenshipids,
  trackCitizenshipEdit,
  citizenships,
  citizenshipsToDelete,
}) => {
  const newCitizenships = data.map(gqlReponseData => {
    const updatedCitizenship = gqlReponseData.updateCandidateRegionalCitizenship.regionalCitizenship
    trackCitizenshipEdit({
      citizenshipStatus: updatedCitizenship.citizenshipStatus,
      countryName: updatedCitizenship.countryName,
      citizenshipId: updatedCitizenship.id,
    })
    return updatedCitizenship
  })
  const editingCitizenshipIdsToRemove = [...editingCitizenshipIds]
  const newCitizenshipIds = newCitizenships.map(citizenship => {
    editingCitizenshipIdsToRemove.splice(editingCitizenshipIdsToRemove.indexOf(citizenship), 1)
    return citizenship.id
  })
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])

  const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
    regionalCitizenship => regionalCitizenship.countryIso2Code,
  )

  citizenships.forEach(citizenship => {
    if (!existingCountryCodes.includes(citizenship.value)) {
      cachedQueryData.candidateProfile.regionalCitizenships.push({
        countryName: citizenship.label,
        countryIso2Code: citizenship.value,
        citizenshipStatus: 'CITIZEN',
      })
    }
  })

  const existingCitizenships = filterOutNewOrDeletedCitizenships({
    citizenshipsToDelete,
    regionalCitizenships: cachedQueryData.candidateProfile.regionalCitizenships,
    newCitizenshipIds,
  })

  cachedQueryData.candidateProfile.regionalCitizenships = [...existingCitizenships, ...newCitizenships]
  queryClient.setQueryData(([CITIZENSHIP_KEY, candidateId], cachedQueryData))
  setEditingCitizenshipids(editingCitizenshipIdsToRemove)
}

const updatedCacheWithDeleteResponse = ({
  deleteCandidateRegionalCitizenship,
  trackCitizenshipDelete,
  queryClient,
  candidateId,
  reset,
  formatInitialValues,
  citizenships,
  citizenshipsToDelete,
}) => {
  const cachedQueryData = queryClient.getQueryData([CITIZENSHIP_KEY, candidateId])
  const updatedRegionalCitizenships = [...cachedQueryData.candidateProfile.regionalCitizenships]
  const deletedIndex = updatedRegionalCitizenships.findIndex(
    regionalCitizenship => regionalCitizenship.id === deleteCandidateRegionalCitizenship.regionalCitizenship.id,
  )
  const deletedRegionalCitizenship = deleteCandidateRegionalCitizenship.regionalCitizenship
  trackCitizenshipDelete({
    citizenshipId: deletedRegionalCitizenship.id,
    citizenshipStatus: deletedRegionalCitizenship.citizenshipStatus,
    countryName: deletedRegionalCitizenship.countryName,
  })
  updatedRegionalCitizenships.splice(deletedIndex, 1)
  const existingCitizenships = filterOutNewOrDeletedCitizenships({
    citizenshipsToDelete,
    regionalCitizenships: updatedRegionalCitizenships,
  })
  cachedQueryData.candidateProfile.regionalCitizenships = [...existingCitizenships]

  const existingCountryCodes = cachedQueryData.candidateProfile.regionalCitizenships.map(
    regionalCitizenship => regionalCitizenship.countryIso2Code,
  )

  citizenships.forEach(citizenship => {
    if (!existingCountryCodes.includes(citizenship.value)) {
      cachedQueryData.candidateProfile.regionalCitizenships.push({
        countryName: citizenship.label,
        countryIso2Code: citizenship.value,
        citizenshipStatus: 'CITIZEN',
      })
    }
  })

  queryClient.setQueryData(([CITIZENSHIP_KEY, candidateId], cachedQueryData))
  reset(
    formatInitialValues({
      regionalCitizenshipsData: cachedQueryData.candidateProfile.regionalCitizenships,
      shouldExcludeNoId: false,
    }),
  )
}

export const deleteWorkAuthOnSuccess = ({
  data,
  queryClient,
  candidateId,
  reset,
  formatInitialValues,
  trackCitizenshipDelete,
  citizenships,
  citizenshipsToDelete,
}) => {
  if (data.length > 0) {
    data.forEach(response => {
      updatedCacheWithDeleteResponse({
        deleteCandidateRegionalCitizenship: response.deleteCandidateRegionalCitizenship,
        trackCitizenshipDelete,
        queryClient,
        candidateId,
        reset,
        formatInitialValues,
        citizenships,
      })
    })
  } else {
    updatedCacheWithDeleteResponse({
      deleteCandidateRegionalCitizenship: data.deleteCandidateRegionalCitizenship,
      trackCitizenshipDelete,
      queryClient,
      candidateId,
      reset,
      formatInitialValues,
      citizenships,
      citizenshipsToDelete,
    })
  }
}
